import { useState } from "react";
import { Phone, Mail, MapPin, ExternalLink, Clock, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAGARO_URL = "https://www.vagaro.com/dbhgg6";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone || "N/A",
        zip_code: "N/A",
        recommended_plan: null,
        source: "contact_form",
      });
      setSent(true);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#0A0A0B]">
      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left — Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#C5A059]" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#C5A059]">
                  Get in Touch
                </span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#F3F4F6] leading-[1.1] mb-6">
                Let's connect.
              </h1>
              <p className="text-base text-[#A1A1AA] leading-relaxed font-light mb-10">
                Have questions about our memberships, services, or availability? 
                Reach out directly or send us a message.
              </p>

              <div className="space-y-6 mb-10">
                <a href="tel:4159946699" data-testid="contact-phone" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-sm bg-[#C5A059]/10 flex items-center justify-center group-hover:bg-[#C5A059]/20 transition-colors">
                    <Phone className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#71717A]">Phone</p>
                    <p className="text-[#F3F4F6] font-medium">(415) 994-6699</p>
                  </div>
                </a>

                <a href="mailto:ltaplett@luxgroom.net" data-testid="contact-email" className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-sm bg-[#C5A059]/10 flex items-center justify-center group-hover:bg-[#C5A059]/20 transition-colors">
                    <Mail className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#71717A]">Email</p>
                    <p className="text-[#F3F4F6] font-medium">ltaplett@luxgroom.net</p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-sm bg-[#C5A059]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#71717A]">Service Area</p>
                    <p className="text-[#F3F4F6] font-medium">San Francisco Bay Area, CA</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-sm bg-[#C5A059]/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#C5A059]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#71717A]">Hours</p>
                    <p className="text-[#F3F4F6] font-medium">Full Service — 24/7</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <p className="text-sm text-[#71717A] mb-3">Follow us</p>
                <div className="flex gap-3">
                  {[
                    { label: "Instagram", url: "https://instagram.com/luxgroom" },
                    { label: "Facebook", url: "https://facebook.com/luxgroom" },
                    { label: "Twitter", url: "https://twitter.com/luxgroom" },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-${s.label.toLowerCase()}`}
                      className="px-4 py-2 rounded-sm bg-white/5 border border-white/10 text-sm text-[#A1A1AA] hover:text-[#C5A059] hover:border-[#C5A059]/30 transition-all"
                    >
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>

              {/* Direct booking CTA */}
              <div className="mt-10 p-6 rounded-md bg-[#141416] border border-white/5">
                <p className="text-sm text-[#A1A1AA] mb-3">Ready to book an appointment?</p>
                <a
                  href={VAGARO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-vagaro-btn"
                  className="inline-flex items-center gap-2 bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-6 py-3 text-sm uppercase tracking-wider"
                >
                  Book on Vagaro <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Right — Form */}
            <div>
              {!sent ? (
                <form onSubmit={handleSubmit} className="p-8 md:p-10 rounded-md bg-[#141416] border border-white/5">
                  <h2 className="font-heading text-xl font-medium text-[#F3F4F6] mb-6">Send us a message</h2>
                  <div className="space-y-5">
                    <div>
                      <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Name *</label>
                      <input
                        data-testid="contact-name"
                        type="text" name="name" value={form.name} onChange={handleChange}
                        placeholder="Your name"
                        className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all h-12 px-4 placeholder:text-[#71717A] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Email *</label>
                      <input
                        data-testid="contact-email-input"
                        type="email" name="email" value={form.email} onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all h-12 px-4 placeholder:text-[#71717A] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Phone (optional)</label>
                      <input
                        data-testid="contact-phone-input"
                        type="tel" name="phone" value={form.phone} onChange={handleChange}
                        placeholder="(415) 555-0000"
                        className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all h-12 px-4 placeholder:text-[#71717A] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-[#A1A1AA] mb-2 font-medium">Message *</label>
                      <textarea
                        data-testid="contact-message"
                        name="message" value={form.message} onChange={handleChange}
                        placeholder="How can we help?"
                        rows={5}
                        className="w-full bg-white/5 border border-white/10 text-white focus:ring-1 focus:ring-[#C5A059] focus:border-[#C5A059] rounded-sm transition-all px-4 py-3 placeholder:text-[#71717A] outline-none resize-none"
                      />
                    </div>
                    <button
                      data-testid="contact-submit-btn"
                      type="submit" disabled={loading}
                      className="w-full bg-[#C5A059] text-black hover:bg-[#D4AF37] font-medium transition-all duration-300 rounded-sm px-8 py-4 text-sm uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? "Sending..." : <><Send className="w-4 h-4" /> Send Message</>}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="p-10 rounded-md bg-[#141416] border border-[#C5A059]/30 glow-gold text-center">
                  <Mail className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
                  <h2 className="font-heading text-2xl font-medium text-[#F3F4F6] mb-3">Message sent!</h2>
                  <p className="text-sm text-[#A1A1AA] mb-2">
                    We'll get back to you shortly. For immediate assistance:
                  </p>
                  <a href="tel:4159946699" className="text-[#C5A059] font-medium text-lg hover:text-[#D4AF37] transition-colors">
                    (415) 994-6699
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
